import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"
import {
  ChartTooltip,
  EmptyHint,
  Panel,
  StatBar,
  StatRow,
  StatTile,
  formatDecimal,
  formatMoney,
  formatPercent,
} from "./statsUi"

// Where a player's money comes from and where it goes. Both breakdowns are
// scaled against the largest line in their own group, so the bars compare
// incomes to incomes and costs to costs rather than to one shared maximum that
// would flatten whichever group happens to be smaller.
const EconomyTab: React.FC<{ stats: IUserLifetimeStatsResponse }> = ({
  stats,
}) => {
  const games = stats.gamesPlayed || 0
  const perGame = (total: number) => (games > 0 ? total / games : 0)

  const income = [
    { label: "Rent collected", value: stats.totalRentCollected, color: "#34D399" },
    { label: "Passing GO", value: stats.totalPassGoCollected, color: "#FBBF24" },
    { label: "Stock returns", value: stats.stockRetrieved, color: "#38BDF8" },
    { label: "Trades", value: stats.moneyTakenInTrades, color: "#A78BFA" },
    { label: "House sales", value: stats.totalFromHouseSales, color: "#FB923C" },
  ].sort((a, b) => b.value - a.value)

  const spending = [
    { label: "Rent paid", value: stats.totalRentPaid, color: "#F87171" },
    { label: "Properties", value: stats.totalSpentOnProperties, color: "#FB923C" },
    { label: "Houses", value: stats.totalSpentOnHouses, color: "#FBBF24" },
    { label: "Auctions", value: stats.moneySpentInAuctions, color: "#A78BFA" },
    { label: "Stock invested", value: stats.stockInvested, color: "#38BDF8" },
    { label: "Tax", value: stats.totalTaxPaid, color: "#94A3B8" },
    { label: "Trades", value: stats.moneyGivenInTrades, color: "#F472B6" },
  ].sort((a, b) => b.value - a.value)

  const maxIncome = Math.max(...income.map((item) => item.value), 1)
  const maxSpending = Math.max(...spending.map((item) => item.value), 1)

  const flow = [
    { name: "Earned", value: stats.totalMoneyGained || 0, color: "#34D399" },
    { name: "Spent", value: stats.totalMoneyLost || 0, color: "#F87171" },
    {
      name: "Net",
      value: stats.derived.netCashFlow,
      color: stats.derived.netCashFlow >= 0 ? "#FBBF24" : "#F87171",
    },
  ]

  return (
    <div className="flex flex-col gap-4 sm:gap-2.5">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-2">
        <StatTile
          label="Total Earned"
          value={formatMoney(stats.totalMoneyGained)}
          sub={`${formatMoney(perGame(stats.totalMoneyGained))} per game`}
          icon="fa-arrow-trend-up"
          accent="#34D399"
        />
        <StatTile
          label="Total Spent"
          value={formatMoney(stats.totalMoneyLost)}
          sub={`${formatMoney(perGame(stats.totalMoneyLost))} per game`}
          icon="fa-arrow-trend-down"
          accent="#F87171"
        />
        <StatTile
          label="Rent Ratio"
          value={
            stats.totalRentPaid > 0
              ? `${formatDecimal(stats.derived.rentRatio, 2)}x`
              : "—"
          }
          sub="collected vs paid"
          icon="fa-scale-balanced"
          accent="#FBBF24"
        />
        <StatTile
          label="Stock P&L"
          value={formatMoney(stats.derived.stockProfit)}
          sub={`${formatMoney(stats.stockInvested)} invested`}
          icon="fa-chart-line"
          accent={stats.derived.stockProfit >= 0 ? "#38BDF8" : "#F87171"}
        />
        <StatTile
          label="Avg Net Worth"
          value={formatMoney(stats.derived.averageNetworth)}
          sub={`${formatMoney(stats.bestNetworth)} best`}
          icon="fa-vault"
          accent="#A78BFA"
        />
      </div>

      <Panel
        title="Cash Flow"
        subtitle="Lifetime money in, money out"
        icon="fa-coins"
        accent="#34D399"
      >
        {games === 0 ? (
          <EmptyHint text="No money has moved yet." />
        ) : (
          <div className="h-56 sm:h-44 mobile:h-[136px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={flow}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.07)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => formatMoney(value)}
                  width={62}
                />
                <ChartTooltip />
                <Bar dataKey="value" name="Amount" radius={[6, 6, 0, 0]}>
                  {flow.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-2.5">
        <Panel
          title="Income Sources"
          subtitle="What pays the bills"
          icon="fa-hand-holding-dollar"
          accent="#34D399"
        >
          <div className="flex flex-col gap-3">
            {income.map((item) => (
              <StatBar
                key={item.label}
                label={item.label}
                value={formatMoney(item.value)}
                fraction={item.value / maxIncome}
                color={item.color}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Where It Goes"
          subtitle="Lifetime outgoings"
          icon="fa-receipt"
          accent="#F87171"
        >
          <div className="flex flex-col gap-3">
            {spending.map((item) => (
              <StatBar
                key={item.label}
                label={item.label}
                value={formatMoney(item.value)}
                fraction={item.value / maxSpending}
                color={item.color}
              />
            ))}
          </div>
        </Panel>
      </div>

      <Panel
        title="Per Game Averages"
        subtitle="Divided across every finished game"
        icon="fa-calculator"
        accent="#38BDF8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-0">
          <StatRow
            label="Money earned"
            value={formatMoney(perGame(stats.totalMoneyGained))}
            color="#34D399"
          />
          <StatRow
            label="Money spent"
            value={formatMoney(perGame(stats.totalMoneyLost))}
            color="#F87171"
          />
          <StatRow
            label="Rent collected"
            value={formatMoney(stats.derived.averageRentPerGame)}
            color="#FBBF24"
          />
          <StatRow
            label="Rent paid"
            value={formatMoney(perGame(stats.totalRentPaid))}
            color="#F87171"
          />
          <StatRow
            label="Collected from GO"
            value={formatMoney(perGame(stats.totalPassGoCollected))}
            color="#FBBF24"
          />
          <StatRow
            label="Tax paid"
            value={formatMoney(perGame(stats.totalTaxPaid))}
            color="#94A3B8"
          />
          <StatRow
            label="Spent on properties"
            value={formatMoney(perGame(stats.totalSpentOnProperties))}
            color="#FB923C"
          />
          <StatRow
            label="Spent on houses"
            value={formatMoney(perGame(stats.totalSpentOnHouses))}
            color="#FBBF24"
          />
          <StatRow
            label="Final net worth"
            value={formatMoney(stats.derived.averageNetworth)}
            color="#A78BFA"
          />
        </div>
        <p className="mt-3 text-xs mobile:text-[11px]/[1.4] text-white/30">
          Bankrupt in {formatPercent(stats.derived.bankruptcyRate, 0)} of games ·{" "}
          {formatDecimal(stats.derived.averageTurnsPerGame, 0)} turns played on
          average
        </p>
      </Panel>
    </div>
  )
}

export default EconomyTab
